<script setup lang="ts">
import { computed } from "vue"

import { getCurrentPage } from "../../../composables/set-page"
import { KirbyBlock } from "../../../queries/blocks"
import { KirbyImageData } from "../../../queries/image"
import { useZoom } from "@/composables/use-zoom"

const props = defineProps<{
  block: KirbyBlock<"image">
  ratio?: number
}>()

const { openZoom } = useZoom()

const blockImage = computed(() => {
  return props.block.content.image[0]
})

const currentImage = computed(() => {
  if (!blockImage.value) return
  const images = getCurrentPage().images as KirbyImageData[]
  if (!images) return
  let image = images.find(image => image.uuid === blockImage.value)
  if (!image) image = images.find(image => image.filename === blockImage.value)
  if (image && props.block.content.alt) image.alt = props.block.content.alt
  return image
})

const getRenderedRect = (el: HTMLImageElement, ratio: number): DOMRect => {
  const r = el.getBoundingClientRect()
  const boxRatio = r.width / r.height
  const imgW = ratio >= boxRatio ? r.width : r.height * ratio
  const imgH = ratio >= boxRatio ? r.width / ratio : r.height
  return new DOMRect(
    r.left + (r.width - imgW) / 2,
    r.top + (r.height - imgH) / 2,
    imgW,
    imgH,
  )
}

const isGif = computed(() => currentImage.value?.mime === "image/gif")
const imgSrc = computed(() =>
  isGif.value ? currentImage.value?.url : currentImage.value?.src,
)
const imgSrcset = computed(() =>
  isGif.value ? undefined : currentImage.value?.srcset,
)
</script>

<template>
  <img
    v-if="currentImage"
    :data-zoom-id="currentImage.uuid"
    :srcset="imgSrcset"
    :src="imgSrc"
    :alt="currentImage.alt || ''"
    sizes="(min-width: 1024px) 200px, (min-width: 768px) 300px, calc(50vw - 60px)"
    class="object-contain w-full h-full cursor-zoom-in transition-opacity"
    loading="lazy"
    draggable="false"
    @click="
      e => {
        const el = e.currentTarget as HTMLImageElement
        openZoom(currentImage!, getRenderedRect(el, currentImage!.ratio), el)
      }
    "
  />

  <div v-else class="error">
    <div v-if="blockImage">{{ blockImage }} not found</div>
    <div v-else>Empty image block.</div>
  </div>
</template>
